import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  ArrowLeft,
  Save,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  FileText,
  Loader,
  AlertTriangle,
  X,
  RefreshCw,
  MessageSquare,
  Zap,
  Plus,
  Trash2,
  Search,
} from "lucide-react";
import { Order } from "../../types";
import { API_BASE_URL } from "../../utils/apiConfig";
import { getStatusDisplayName, CANCEL_REASONS } from "../../utils/statusMapper";
import { api } from "../../services/api";
import { getCountryName } from "../../utils/countryNames";

interface LeadEditPageProps {
  orderId: number;
  onBack: () => void;
  onSave: () => void;
}

type StatusAction = "save" | "approve" | "callLater" | "cancel" | "packageCompleted" | "completed" | "returned" | "returnToCallCenter";

interface OrderProduct {
  id: string; // Temporary ID for table rows
  variantId?: string;
  productName: string;
  quantity: number;
  price: number;
  currency: string;
  sku?: string;
}

const LeadEditPage: React.FC<LeadEditPageProps> = ({
  orderId,
  onBack,
  onSave,
}) => {
  // BEX dispatch removed for Greek-CC (Greece uses ACS only)
  // const dispatch = useAppDispatch();
  const [order, setOrder] = useState<Order | null>(null);
  const [formData, setFormData] = useState<Partial<Order>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Order products state
  const [orderProducts, setOrderProducts] = useState<OrderProduct[]>([]);
  
  // Product autocomplete state
  const [productSearch, setProductSearch] = useState("");
  const [productSuggestions, setProductSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  
  // Status workflow state
  const [statusAction, setStatusAction] = useState<StatusAction>("save");
  
  // Next call scheduling (for "Call later")
  const [nextCallDate, setNextCallDate] = useState("");
  const [nextCallTime, setNextCallTime] = useState({ hours: "01", minutes: "30" });
  const [nextCallTimezone, setNextCallTimezone] = useState(false);
  const [nextCallProblem, setNextCallProblem] = useState("");
  
  // Cancel fields
  const [cancelReason, setCancelReason] = useState<number | "">("");
  const [cancelStage, setCancelStage] = useState<number | "">("");

  // Determine if order is approved (status 6 OR status 5 with reason 2 "Changed his mind" and stage 235)
  const isApproved = order && (
    order.status === 6 || 
    (order.status === 5 && 
     ((order as any).cancelReason === 2 || order.reason === 2) && 
     (order.stage !== 0))
  );

  // Fetch product suggestions
  const fetchProductSuggestions = useCallback(async (searchTerm: string) => {
    if (!searchTerm || searchTerm.length < 2) {
      setProductSuggestions([]);
      return;
    }

    try {
      setLoadingProducts(true);
      // Fetch more variants for better search results
      const response = await api.getVariants({
        page: 1,
        limit: 100, // Fetch more variants for better search
        enabled: true,
      });

      if (response.success && response.data?.variants) {
        const variants = response.data.variants;
        const searchLower = searchTerm.toLowerCase();
        const filtered = variants.filter((variant: any) => {
          return (
            variant.variantName?.toLowerCase().includes(searchLower) ||
            variant.baseProductName?.toLowerCase().includes(searchLower) ||
            variant.variantSKU?.toLowerCase().includes(searchLower) ||
            variant.category_names?.toLowerCase().includes(searchLower)
          );
        });
        setProductSuggestions(filtered.slice(0, 10)); // Limit to 10 suggestions for UI
      }
    } catch (err) {
      console.error("Error fetching products:", err);
      setProductSuggestions([]);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  // Debounced product search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearch) {
        fetchProductSuggestions(productSearch);
      } else {
        setProductSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [productSearch, fetchProductSuggestions]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.product-autocomplete-container')) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSuggestions]);

  // Fetch order details
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        const response = await fetch(
          `${API_BASE_URL}/api/enhanced-orders/paginated?search=${orderId}&limit=1`,
          {
            credentials: "include",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.success && data.data?.orders?.[0]) {
          const fetchedOrder = data.data.orders[0];
          setOrder(fetchedOrder);
          setFormData({
            status: fetchedOrder.status,
            name: fetchedOrder.name,
            phone: fetchedOrder.phone,
            email: fetchedOrder.email,
            country: fetchedOrder.country,
            area: fetchedOrder.area || "",
            city: fetchedOrder.city,
            street: fetchedOrder.street || "",
            addr: fetchedOrder.addr,
            index: fetchedOrder.index,
            comment: fetchedOrder.comment,
          });
          
          // Initialize order products from goods array
          if (fetchedOrder.goods && fetchedOrder.goods.length > 0) {
            // Use goods array if available
            const products = fetchedOrder.goods.map((good: any, index: number) => ({
              id: `good-${index}`,
              productName: good.name || fetchedOrder.offername || "",
              quantity: good.count || 1,
              price: good.price || 0, // Unit price from goods[n].price
              currency: fetchedOrder.currency || "MKD",
            }));
            setOrderProducts(products);
          } else if (fetchedOrder.offername) {
            // Fallback to legacy fields if goods array is not available
            setOrderProducts([{
              id: "1",
              productName: fetchedOrder.offername,
              quantity: fetchedOrder.count || 1,
              price: fetchedOrder.price || 0,
              currency: fetchedOrder.currency || "MKD",
            }]);
          }
          
          // Set cancel reason and stage if order is cancelled
          if (fetchedOrder.status === 5) {
            setCancelReason((fetchedOrder as any).cancelReason || fetchedOrder.reason || "");
            setCancelStage(fetchedOrder.stage || "");
            setStatusAction("cancel");
          }
          
          // Set next call data if order is callback
          if (fetchedOrder.status === 3 && fetchedOrder.nextCall) {
            setNextCallDate(fetchedOrder.nextCall.date || "");
            if (fetchedOrder.nextCall.time) {
              const [hours, minutes] = fetchedOrder.nextCall.time.split(":");
              setNextCallTime({ hours: hours || "01", minutes: minutes || "30" });
            }
            setNextCallTimezone(fetchedOrder.nextCall.timezone === "+00:00");
            setNextCallProblem(fetchedOrder.nextCall.problem || "");
            setStatusAction("callLater");
          }
          
          // Set status action based on current status
          const isAlreadyApproved = fetchedOrder.status === 6 || 
            (fetchedOrder.status === 5 && 
             ((fetchedOrder as any).cancelReason === 2 || fetchedOrder.reason === 2) && 
             fetchedOrder.stage === 235);
          if (isAlreadyApproved) {
            setStatusAction("save");
          }
        } else {
          throw new Error("Order not found");
        }
      } catch (err) {
        console.error("Error fetching order:", err);
        setError(err instanceof Error ? err.message : "Failed to load order");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  // Calculate total price
  const totalPrice = useMemo(() => {
    return orderProducts.reduce((sum, product) => {
      return sum + (product.price * product.quantity);
    }, 0);
  }, [orderProducts]);

  // Get main currency (use first product's currency or default)
  const mainCurrency = useMemo(() => {
    return orderProducts[0]?.currency || order?.currency || "MKD";
  }, [orderProducts, order]);

  // Handle adding a product
  const handleAddProduct = useCallback((variant: any) => {
    const country = formData.country || order?.country || "MK";
    const countryPrice = variant.countryPrices?.[country] || 
                        variant.countryPrices?.[country.toUpperCase()] ||
                        { price: variant.price || 0, currency: variant.currency || "MKD" };
    
    const newProduct: OrderProduct = {
      id: Date.now().toString(),
      variantId: variant._id,
      productName: variant.variantName || variant.baseProductName || "",
      quantity: 1,
      price: countryPrice.price || variant.price || 0,
      currency: countryPrice.currency || variant.currency || "MKD",
      sku: variant.variantSKU,
    };
    
    setOrderProducts(prev => [...prev, newProduct]);
    setProductSearch("");
    setShowSuggestions(false);
    setProductSuggestions([]);
  }, [formData.country, order?.country]);

  // Handle removing a product
  const handleRemoveProduct = useCallback((id: string) => {
    setOrderProducts(prev => prev.filter(p => p.id !== id));
  }, []);

  // Handle updating a product
  const handleUpdateProduct = useCallback((id: string, field: keyof OrderProduct, value: any) => {
    setOrderProducts(prev => prev.map(p => {
      if (p.id === id) {
        return { ...p, [field]: value };
      }
      return p;
    }));
  }, []);

  const handleSave = async () => {
    if (!order) return;

    // Validation
    if (statusAction === "cancel" && !cancelReason) {
      setError("Cancel reason is required when canceling an order");
      return;
    }

    if (orderProducts.length === 0) {
      setError("At least one product is required");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem("authToken");
      
      // Calculate total count from all goods
      const totalCount = orderProducts.reduce((sum, p) => sum + p.quantity, 0);
      
      // Build update payload based on status action
      const updatePayload: any = {
        ...formData,
        // Update order with first product info (for backward compatibility)
        offername: orderProducts[0]?.productName || order?.offername || "",
        count: totalCount, // Total count = sum of all goods counts
        price: totalPrice, // Total price = sum of (goods[n].price * goods[n].count)
        currency: mainCurrency,
        // Store goods array with proper structure (goods[n].price is unit price, goods[n].count is quantity)
        goods: orderProducts.map(p => ({
          name: p.productName,
          count: p.quantity, // Quantity for this good
          price: p.price, // Unit price for this good
        })),
      };

      // Handle status action
      switch (statusAction) {
        case "save":
          break;
          
        case "approve":
          updatePayload.status = 6; // Packing
          break;
          
        case "callLater":
          updatePayload.status = 3; // Callback
          const nextCallTimeStr = `${nextCallTime.hours.padStart(2, "0")}:${nextCallTime.minutes.padStart(2, "0")}`;
          updatePayload.nextCall = {
            date: nextCallDate,
            time: nextCallTimeStr,
            timezone: nextCallTimezone ? "+00:00" : "",
            problem: nextCallProblem || undefined,
          };
          break;
          
        case "cancel":
          updatePayload.status = 5; // Cancel
          updatePayload.cancelReason = Number(cancelReason);
          if (cancelStage) {
            updatePayload.stage = Number(cancelStage);
          }
          break;
          
        case "packageCompleted":
          updatePayload.status = 7; // Sending
          break;
          
        case "completed":
          updatePayload.status = 10; // Complete
          break;
          
        case "returned":
          updatePayload.status = 11; // Return
          break;
          
        case "returnToCallCenter":
          updatePayload.status = 2; // Processing
          updatePayload.cancelReason = undefined;
          updatePayload.reason = undefined;
          updatePayload.stage = undefined;
          break;
      }

      const response = await fetch(
        `${API_BASE_URL}/api/enhanced-orders/${orderId}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatePayload),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to save changes");
      }

      onSave();
      onBack();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (
    field: keyof Order,
    value: string | number | undefined
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Generate time options
  const hourOptions = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, "0"));
  const minuteOptions = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, "0"));

  // Cancel reason options
  const cancelReasonOptions = Object.entries(CANCEL_REASONS).map(([code, name]) => ({
    value: Number(code),
    label: name,
  }));

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading lead details...</p>
        </div>
      </div>
    );
  }

  if (error && !order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Error Loading Lead
          </h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={onBack}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const getTaskLabel = (task: string): string => {
    const taskLabels: { [key: string]: string } = {
      'update_address': 'Update Address',
      'sell_new_product': 'Try to Sell New Product',
      'collect_more_data': 'Collect More Data',
      'new_dose': 'Resell'
    };
    return taskLabels[task] || task;
  };

  const currentStatus = order ? getStatusDisplayName(order) : "Unknown";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="inline-flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Leads
              </button>
              <div className="h-6 w-px bg-gray-300" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Lead #{orderId}
                </h1>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 bg-blue-100 text-blue-800">
                  {currentStatus}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm text-red-800">{error}</p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setError(null)}
                  className="inline-flex text-red-400 hover:text-red-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-6 space-y-6">
            {/* 1. Order Information - FIRST */}
            <div className="border-b border-gray-200 pb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Order Information
                </h3>
              </div>

              {/* Product Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Unit Price
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orderProducts.map((product) => (
                      <tr key={product.id}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {product.productName}
                          </div>
                          {product.sku && (
                            <div className="text-xs text-gray-500">SKU: {product.sku}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <input
                            type="number"
                            value={product.quantity}
                            onChange={(e) => handleUpdateProduct(product.id, "quantity", parseInt(e.target.value) || 1)}
                            min="1"
                            className="w-20 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              value={product.price}
                              onChange={(e) => handleUpdateProduct(product.id, "price", parseFloat(e.target.value) || 0)}
                              step="0.01"
                              className="w-24 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-500">{product.currency}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {(product.price * product.quantity).toFixed(2)} {product.currency}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleRemoveProduct(product.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={3} className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                        Total:
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900">
                        {totalPrice.toFixed(2)} {mainCurrency}
                      </td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Add Product Autocomplete */}
              <div className="mt-4 relative product-autocomplete-container">
                <div className="flex items-center space-x-2">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      value={productSearch}
                      onChange={(e) => {
                        setProductSearch(e.target.value);
                        setShowSuggestions(true);
                      }}
                      onFocus={() => setShowSuggestions(true)}
                      placeholder="Search and add product (type at least 2 characters)..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {showSuggestions && productSuggestions.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {productSuggestions.map((variant) => {
                          const country = formData.country || order?.country || "MK";
                          const countryCode = country.toUpperCase();
                          const countryPrice = variant.countryPrices?.[countryCode] || 
                                              variant.countryPrices?.[country] ||
                                              { price: variant.price || 0, currency: variant.currency || "MKD" };
                          return (
                            <button
                              key={variant._id}
                              onClick={() => handleAddProduct(variant)}
                              className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-100 last:border-b-0"
                            >
                              <div className="font-medium text-gray-900">
                                {variant.variantName || variant.baseProductName}
                              </div>
                              <div className="text-sm text-gray-500">
                                {countryPrice.price.toFixed(2)} {countryPrice.currency}
                                {variant.variantSKU && ` • SKU: ${variant.variantSKU}`}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {loadingProducts && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <Loader className="w-4 h-4 text-gray-400 animate-spin" />
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (productSuggestions.length > 0) {
                        handleAddProduct(productSuggestions[0]);
                      }
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!productSearch || productSuggestions.length === 0 || loadingProducts}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Product
                  </button>
                </div>
              </div>

              {/* Created Date */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-2" />
                  Created
                </label>
                <input
                  type="text"
                  value={order ? new Date(order.time).toLocaleString() : ""}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  disabled
                />
              </div>
            </div>

            {/* 2. Customer Information - SECOND */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Customer Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Customer Name
                  </label>
                  <input
                    type="text"
                    value={formData.name || ""}
                    onChange={(e) => handleChange("name", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Customer name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="w-4 h-4 inline mr-2" />
                    Phone
                  </label>
                  <input
                    type="text"
                    value={formData.phone || ""}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Phone number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email || ""}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Email address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin className="w-4 h-4 inline mr-2" />
                    Country
                  </label>
                  <input
                    type="text"
                    value={getCountryName(formData.country || order?.country)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    disabled
                  />
                </div>

                {/* BEX Address Selection removed for Greek-CC (Greece uses ACS only) */}
                
                {/* Standard Address Fields (for Greece) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.city || ""}
                    onChange={(e) => handleChange("city", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="City"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Area
                  </label>
                  <input
                    type="text"
                    value={formData.area || ""}
                    onChange={(e) => handleChange("area", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Area"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Street
                  </label>
                  <input
                    type="text"
                    value={formData.street || ""}
                    onChange={(e) => handleChange("street", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Street"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address Number
                  </label>
                  <input
                    type="text"
                    value={formData.addr || ""}
                    onChange={(e) => handleChange("addr", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Address number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Zip Code
                  </label>
                  <input
                    type="text"
                    value={formData.index || ""}
                    onChange={(e) => handleChange("index", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Zip code"
                  />
                </div>
              </div>

              {/* Task Instruction (if from leads) */}
              {order?.leadTask && (
                <div className="mt-6 mb-4 p-3 bg-purple-50 border border-purple-200 rounded-md">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <span className="text-purple-600 text-lg">📋</span>
                    </div>
                    <div className="ml-3 flex-1">
                      <h4 className="text-sm font-medium text-purple-900 mb-1">
                        Task for Operator
                      </h4>
                      <p className="text-sm text-purple-800">
                        {getTaskLabel(order.leadTask)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Comment at bottom of Customer Information */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FileText className="w-4 h-4 inline mr-2" />
                  Comment / Notes
                </label>
                <textarea
                  value={formData.comment || ""}
                  onChange={(e) => handleChange("comment", e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Add notes about this lead..."
                />
              </div>
            </div>

            {/* 3. Status Actions - THIRD */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Status Actions
              </h3>
              
              {!isApproved ? (
                <div className="space-y-3">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="statusAction"
                      value="save"
                      checked={statusAction === "save"}
                      onChange={(e) => setStatusAction(e.target.value as StatusAction)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      Save the order info without status changes
                    </span>
                  </label>

                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="statusAction"
                      value="approve"
                      checked={statusAction === "approve"}
                      onChange={(e) => setStatusAction(e.target.value as StatusAction)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-3 text-sm font-medium text-green-700">
                      Approve order
                    </span>
                  </label>

                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="statusAction"
                      value="callLater"
                      checked={statusAction === "callLater"}
                      onChange={(e) => setStatusAction(e.target.value as StatusAction)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      Call later
                    </span>
                  </label>

                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="statusAction"
                      value="cancel"
                      checked={statusAction === "cancel"}
                      onChange={(e) => setStatusAction(e.target.value as StatusAction)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-3 text-sm font-medium text-red-700">
                      Cancel the order due to customer request
                    </span>
                  </label>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 mb-3">
                    Order is approved. Select next status:
                  </p>
                  
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="statusAction"
                      value="save"
                      checked={statusAction === "save"}
                      onChange={(e) => setStatusAction(e.target.value as StatusAction)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      Save the order info without status changes
                    </span>
                  </label>

                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="statusAction"
                      value="packageCompleted"
                      checked={statusAction === "packageCompleted"}
                      onChange={(e) => setStatusAction(e.target.value as StatusAction)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      disabled={!order?.trackingId}
                    />
                    <span className={`ml-3 text-sm ${order?.trackingId ? "text-gray-700" : "text-gray-400"}`}>
                      Package completed, move to "Send"
                      {!order?.trackingId && " (requires tracking number)"}
                    </span>
                  </label>

                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="statusAction"
                      value="completed"
                      checked={statusAction === "completed"}
                      onChange={(e) => setStatusAction(e.target.value as StatusAction)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      Order completed and paid
                    </span>
                  </label>

                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="statusAction"
                      value="returned"
                      checked={statusAction === "returned"}
                      onChange={(e) => setStatusAction(e.target.value as StatusAction)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      Order returned to sender
                    </span>
                  </label>

                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="statusAction"
                      value="cancel"
                      checked={statusAction === "cancel"}
                      onChange={(e) => setStatusAction(e.target.value as StatusAction)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-3 text-sm font-medium text-red-700">
                      Cancel the order due to customer request
                    </span>
                  </label>
                </div>
              )}
            </div>

            {/* Next Call Scheduling (for Call later) */}
            {statusAction === "callLater" && (
              <div className="border-b border-gray-200 pb-6">
                <div className="flex items-center mb-3">
                  <RefreshCw className="w-5 h-5 text-gray-400 mr-2" />
                  <h3 className="text-sm font-medium text-gray-700">Next call</h3>
                </div>
                
                <div className="ml-7 space-y-3">
                  <div>
                    <input
                      type="date"
                      value={nextCallDate}
                      onChange={(e) => setNextCallDate(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">at</span>
                    <select
                      value={nextCallTime.hours}
                      onChange={(e) => setNextCallTime(prev => ({ ...prev, hours: e.target.value }))}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {hourOptions.map((hour) => (
                        <option key={hour} value={hour}>{hour}</option>
                      ))}
                    </select>
                    <span className="text-sm text-gray-700">:</span>
                    <select
                      value={nextCallTime.minutes}
                      onChange={(e) => setNextCallTime(prev => ({ ...prev, minutes: e.target.value }))}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {minuteOptions.map((minute) => (
                        <option key={minute} value={minute}>{minute}</option>
                      ))}
                    </select>
                    
                    <label className="flex items-center ml-4 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={nextCallTimezone}
                        onChange={(e) => setNextCallTimezone(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">+00:00</span>
                    </label>
                    
                    <div className="ml-4 flex items-center">
                      <MessageSquare className="w-4 h-4 text-gray-400 mr-2" />
                      <select
                        value={nextCallProblem}
                        onChange={(e) => setNextCallProblem(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">— Problem —</option>
                        <option value="No answer">No answer</option>
                        <option value="Busy">Busy</option>
                        <option value="Wrong number">Wrong number</option>
                        <option value="Language barrier">Language barrier</option>
                        <option value="Need more time">Need more time</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Cancel Reason and Stage (for Cancel) */}
            {statusAction === "cancel" && (
              <div className="border-b border-gray-200 pb-6">
                <div className="ml-7 space-y-4">
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                      <X className="w-4 h-4 text-red-500 mr-2" />
                      Decline reason <span className="text-red-500 ml-1">*</span>
                    </label>
                    <select
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value ? Number(e.target.value) : "")}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select reason</option>
                      {cancelReasonOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {cancelReason && (
                    <div>
                      <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                        <Zap className="w-4 h-4 text-gray-400 mr-2" />
                        Stage <span className="text-gray-500 text-xs ml-1">(Optional)</span>
                      </label>
                      <select
                        value={cancelStage}
                        onChange={(e) => setCancelStage(e.target.value ? Number(e.target.value) : "")}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">— keep current —</option>
                        <option value={235}>235 (Other)</option>
                      </select>
                      {cancelReason === 2 && cancelStage === 235 && (
                        <p className="mt-1 text-xs text-green-600 font-medium">
                          ✓ Order will be approved for shipping (Changed his mind + Stage 235 Other)
                        </p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        Select 235 (Other) to approve the cancelled order for shipping. Leave empty to keep current stage.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Return to Call Center option (for Cancelled orders) */}
            {order?.status === 5 && (
              <div className="border-b border-gray-200 pb-6">
                <div className="ml-7">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="statusAction"
                      value="returnToCallCenter"
                      checked={statusAction === "returnToCallCenter"}
                      onChange={(e) => {
                        setStatusAction(e.target.value as StatusAction);
                        setCancelReason("");
                        setCancelStage("");
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-3 text-sm font-medium text-blue-700">
                      Return to call center (Processing)
                    </span>
                  </label>
                  <p className="ml-7 mt-2 text-xs text-gray-500">
                    Returns the order to Processing status, allowing it to go through the cycle again.
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons - AT BOTTOM */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={onBack}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={
                  saving || 
                  (statusAction === "cancel" && !cancelReason)
                }
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadEditPage;
